/* ──────────────────────────────────────────────────────────────
   src/hooks/useReunionReglas.ts
   React-Query flavour para reglas de agendación
   ────────────────────────────────────────────────────────────── */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchReunionReglas,
  fetchReunionRegla,
  createReunionRegla,
  updateReunionRegla,
  deleteReunionRegla,
  type ReunionReglaAgendacion,
  type ReunionReglaFilters,
  type ReunionReglaSavePayload,
} from '../api/reunionReglas.api';

const LIST_KEY = 'reunionReglas';

export function useReunionReglasList(filters: ReunionReglaFilters = {}) {
  const { data = [], isFetching: loading } = useQuery({
    queryKey: [LIST_KEY, filters],
    queryFn: () => fetchReunionReglas(filters),
    staleTime: 60_000,
  });

  return { rows: data, loading };
}

export function useReunionRegla(id?: number | null) {
  const enabled = !!id;
  const { data, isFetching: loading } = useQuery({
    queryKey: ['reunionRegla', id],
    queryFn: () => fetchReunionRegla(id as number),
    enabled,
  });

  return { row: data, loading };
}

export function useCreateReunionRegla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: ReunionReglaSavePayload) => createReunionRegla(payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
    },
  });
}

export function useUpdateReunionRegla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: number; payload: ReunionReglaSavePayload }) =>
      updateReunionRegla(input.id, input.payload),
    onSuccess: (_, { id }) => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
      qc.invalidateQueries({ queryKey: ['reunionRegla', id] });
    },
  });
}

export function useDeleteReunionRegla() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteReunionRegla(id),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [LIST_KEY] });
    },
  });
}

/** Tipo que usaremos en el formulario/diálogo */
export interface ReunionReglaFormValues {
  tipoReunion: string;
  rolStaffDestino: string;
  canalPreferente: string | null;
  diasDesdeEventoMin: number | null;
  diasDesdeEventoMax: number | null;
  duracionMinutos: number | null;
  activa: boolean;
  nombre?: string | null;
  idUsuario?: number | null;
  idCentro?: number | null;
  esRecurrente?: boolean;
  frecuenciaSemanas?: number | null;
  usarCanalDeClase?: boolean;
  idServicioExterno?: number | null;
  servicioId?: number | null;
  esWc1?: boolean;
  esWc2?: boolean;
  calculoFecha?: 'relativo_clase' | 'semana_siguiente' | 'dias_desde_evento' | null;
  minutosRelativoClase?: number | null;
}

/** Normaliza una regla a los valores del formulario */
export function reglaToFormValues(r?: ReunionReglaAgendacion | null): ReunionReglaFormValues {
  if (!r) {
    return {
      tipoReunion: '',
      rolStaffDestino: '',
      canalPreferente: 'indistinto',
      diasDesdeEventoMin: 0,
      diasDesdeEventoMax: 7,
      duracionMinutos: 45,
      activa: true,
      nombre: null,
      idUsuario: null,
      idCentro: null,
      esRecurrente: false,
      frecuenciaSemanas: null,
      usarCanalDeClase: false,
      idServicioExterno: null,
      servicioId: null,
      esWc1: false,
      esWc2: false,
      calculoFecha: null,
      minutosRelativoClase: null,
    };
  }
  return {
    tipoReunion: r.tipoReunion ?? '',
    rolStaffDestino: r.rolStaffDestino ?? '',
    canalPreferente: (r.canalPreferente as string | null) ?? 'indistinto',
    diasDesdeEventoMin: r.diasDesdeEventoMin ?? 0,
    diasDesdeEventoMax: r.diasDesdeEventoMax ?? 7,
    duracionMinutos: r.duracionMinutos ?? 45,
    activa: !!r.activa,
    nombre: r.nombre ?? null,
    idUsuario: r.idUsuario ?? null,
    idCentro: r.idCentro ?? null,
    esRecurrente: r.esRecurrente ?? false,
    frecuenciaSemanas: r.frecuenciaSemanas ?? null,
    usarCanalDeClase: r.usarCanalDeClase ?? false,
    idServicioExterno: r.idServicioExterno ?? null,
    servicioId: r.servicioId ?? null,
    esWc1: r.esWc1 ?? false,
    esWc2: r.esWc2 ?? false,
    calculoFecha: r.calculoFecha ?? null,
    minutosRelativoClase: r.minutosRelativoClase ?? null,
  };
}

/** Convierte los valores del formulario al payload del backend */
export function formValuesToPayload(v: ReunionReglaFormValues): ReunionReglaSavePayload {
  const toNumOrNull = (x: unknown): number | null => {
    if (x === null || x === undefined || x === '') return null;
    const n = Number(x);
    return Number.isFinite(n) ? n : null;
  };

  return {
    tipoReunion: v.tipoReunion,
    rolStaffDestino: v.rolStaffDestino,
    canalPreferente: v.canalPreferente ?? null,
    diasDesdeEventoMin: toNumOrNull(v.diasDesdeEventoMin),
    diasDesdeEventoMax: toNumOrNull(v.diasDesdeEventoMax),
    duracionMinutos: toNumOrNull(v.duracionMinutos),
    activa: !!v.activa,
    nombre: v.nombre ?? null,
    idUsuario: toNumOrNull(v.idUsuario),
    idCentro: toNumOrNull(v.idCentro),
    esRecurrente: v.esRecurrente ?? false,
    frecuenciaSemanas: toNumOrNull(v.frecuenciaSemanas),
    usarCanalDeClase: v.usarCanalDeClase ?? false,
    idServicioExterno: toNumOrNull(v.idServicioExterno),
    servicioId: toNumOrNull(v.servicioId),
    esWc1: v.esWc1 ?? false,
    esWc2: v.esWc2 ?? false,
    calculoFecha: v.calculoFecha ?? null,
    minutosRelativoClase: toNumOrNull(v.minutosRelativoClase),
  };
}
